import { AutoMode, Campaign, Groups } from "@mui/icons-material";
import { Card, Chip, Fade, Skeleton, styled, SxProps, Theme, Tooltip } from "@mui/material";
import TransitionGroup from "react-transition-group/TransitionGroup";
import { Rule } from "../../../App/Typings/DynamicGroupRules";
import { IArchivedGroup, IGroup } from "../../../App/Typings/Groups";
import DisplayGroupCard from "./DisplayGroupCard";

const StyledTransitionGroup = styled(TransitionGroup)({});

export const DisplayGroupList = ({
  groups,
  handleArchiveGroup,
}: {
  groups: IGroup[];
  handleArchiveGroup: (groupId: string) => void;
}) => {
  return (
    <StyledTransitionGroup
      sx={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: {
          xs: "100%",
          sm: "repeat(2, 1fr)",
          lg: "repeat(3, 1fr)",
          xl: "repeat(4, 1fr)",
        },
        gridTemplateRows: "auto",
        gap: 2,
      }}
    >
      {groups.map((group: IGroup) => {
        return (
          <Fade key={group.id} unmountOnExit>
            {/* Have the Card component here and not in child component otherwise it doesn't work with the Fade component */}
            <Card
              variant="outlined"
              data-test={"candidategroup-" + group.id}
              sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}
            >
              <DisplayGroupCard
                group={group}
                handleArchiveGroup={handleArchiveGroup}
                containsDynamic={groups.filter((c) => c.isDynamic).length > 0}
              />
            </Card>
          </Fade>
        );
      })}
    </StyledTransitionGroup>
  );
};

export const CardSkeleton = ({ keyName }: { keyName: string }) => {
  return (
    <Card
      key={keyName}
      variant="outlined"
      sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", padding: 1 }}
    >
      <Skeleton sx={{ width: "100%", height: "70px" }} variant="text"></Skeleton>
      <Skeleton sx={{ width: "100%", height: "120px" }} variant="rectangular"></Skeleton>
      <Skeleton sx={{ width: "100%", height: "50px" }} variant="text"></Skeleton>
    </Card>
  );
};

export const GroupChip = ({ group, chipSx }: { group: IGroup | IArchivedGroup; chipSx?: SxProps<Theme> }) => {
  const type = getGroupType(group);

  return (
    <Tooltip placement="top" title={`${type} Group`}>
      <Chip
        label={type}
        size="small"
        icon={groupIcon(type)}
        color={getChipColor(type)}
        sx={{ padding: "5px", maxWidth: "90%", ...chipSx }}
      />
    </Tooltip>
  );
};

const groupIcon = (type: string) => {
  switch (type) {
    case "Dynamic":
      return <AutoMode />;
    case "Form":
      return <Campaign />;
    case "Static":
    default:
      return <Groups />;
  }
};

const getChipColor = (type: string) => {
  switch (type) {
    case "Dynamic":
      return "captivateSecondary";
    case "Form":
      return "captivatePrimary";
    case "Static":
    default:
      return "motivatePrimary";
  }
};

const getGroupType = (group: IGroup | IArchivedGroup) => {
  if (group.isDynamic) {
    return "Dynamic";
  } else if ((group as IArchivedGroup).isForm || (group as IGroup).campaignId) {
    return "Form";
  }
  return "Static";
};

export const getRuleFields = (rules: Rule[]) => {
  let fields: string[] = [];

  for (let i = 0; i < rules?.length ?? 0; i++) {
    if (rules[i].rules && rules[i].rules.length > 0) {
      fields = fields.concat(getRuleFields(rules[i].rules));
    }
    if (rules[i].filter.field !== "") {
      let ruleField = toCapitalizedWords(rules[i].filter.field);
      if (ruleField.toLowerCase().includes("custom field")) {
        // increase custom field number by 1, since it doesn't diplay as 0 index on form editing
        ruleField =
          ruleField.substring(0, ruleField.lastIndexOf(" ")) +
          " " +
          (Number.parseInt(ruleField.substring(ruleField.lastIndexOf(" ")).trim()) + 1);
      }
      if (!fields.includes(ruleField)) {
        fields.push(ruleField);
      }
    }
  }

  return fields;
};

const toCapitalizedWords = (name: string) => {
  name = name.substring(name.lastIndexOf("."));
  var words = name.match(/[A-Za-z0-9][a-z]*/g) || [];

  return words.map(capitalize).join(" ");
};

const capitalize = (word: string) => {
  return word.charAt(0).toUpperCase() + word.substring(1);
};
